import { shiftFinancialTotals } from "../../helpers/shiftFinancialTotals";
import { deliveryOutstanding } from "../../helpers/customerOutstanding";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaCashRegister, FaCheckCircle, FaCreditCard, FaMoneyBillWave, FaPrint, FaReceipt, FaUndoAlt, FaUserClock } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, Col, Container, Input, Label, Row, Spinner } from "reactstrap";
import jsPDF from "jspdf";
import { shiftActions } from "../../sagas/shiftSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import customerReturnService from "../../services/customerReturnService";
import { formatPrice } from "../../helpers/utility";
import { printPdfInHiddenIframe } from "../../helpers/printFrame";
import DocumentPagination from "../Common/DocumentPagination";
import Swal from "sweetalert2";
import "./ShiftPage.css";
import useSystemSettings from "../../hooks/useSystemSettings";
import { getReceiptPaymentSummary as receiptPaymentSummary } from "../../helpers/stampDuty";

const toNumber = (value) => Number(value || 0);
const getCashAmount = (bl) => toNumber(bl?.montantKeptCash ?? bl?.montantPayeCash ?? bl?.montantPayeEspece);
const getCardAmount = (bl) => toNumber(bl?.montantPayeCarte);
const getReturnCashAmount = (returnItem) => toNumber(returnItem?.montantPayeCash);
const getReturnCardAmount = (returnItem) => toNumber(returnItem?.montantPayeCarte);
const documentStatus = (status) => ({
  LIVRE: "Livré", EN_PREPARATION: "En préparation", VALIDE: "Confirmé",
  COMPTABILISE: "Confirmé", ANNULE: "Annulé", BROUILLON: "Brouillon",
}[status] || status || "—");
const differenceLabel = (value) => Number(value) === 0
  ? "Le montant est correct"
  : `${formatPrice(Math.abs(value))} ${Number(value) < 0 ? "manquent" : "en plus"}`;
const isNumericSearch = (value) => {
  if (!value) return false;
  return !Number.isNaN(Number(String(value).replace(",", ".")));
};
const amountSortLabels = {
  total: "Montant de la vente",
  paid: "Montant payé",
  cash: "Espèces",
  card: "Carte",
};
const getModeReglementLabel = (mode) => {
  switch (mode) {
    case "ESPECES":
      return "Especes";
    case "CARTE_BANCAIRE":
      return "Carte bancaire";
    case "CHEQUE":
      return "Cheque";
    case "VIREMENT":
      return "Virement";
    case "TRAITE":
      return "Traite";
    case "EN_COMPTE":
      return "En compte";
    default:
      return mode || "-";
  }
};
const ShiftDetails = () => {
  const { droitTimbreEnabled } = useSystemSettings();
  const getReceiptPaymentSummary = (data) => receiptPaymentSummary(data, droitTimbreEnabled);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const editMode = location.state?.editMode;
  const shift = location.state?.shift;

  const { summary, loading } = useSelector((state) => state.shift);
  const {
    list = [],
    loading: deliveryLoading,
    totalPages,
    pageNumber,
    pageSize,
    totalElements,
  } = useSelector((state) => state.deliveryNotes);
  const { businessName, address, telephone } = useSelector((state) => state.auth);

  const [closingCash, setClosingCash] = useState(0);
  const [closingCard, setClosingCard] = useState(0);
  const [salesFilters, setSalesFilters] = useState({ page: 0, size: 10 });
  const [salesSearch, setSalesSearch] = useState("");
  const [salesPaymentFilter, setSalesPaymentFilter] = useState("all");
  const [salesSort, setSalesSort] = useState({ field: null, direction: "desc" });
  const [linkedReturns, setLinkedReturns] = useState({ data: {}, loading: true, error: false });
  const [noTicketReturns, setNoTicketReturns] = useState({ items: [], loading: true, error: false });
  const [returnsRefresh, setReturnsRefresh] = useState(0);
  const references = JSON.stringify(list.map((bl) => bl.numero).filter(Boolean));

  const isValidated = summary?.status === "VALIDATED";
  const isClosed = summary?.status === "CLOSED";

  useEffect(() => {
    if (!shift?.id) return;
    dispatch(shiftActions.fetchSummary({ shiftId: shift.id }));
  }, [shift?.id, dispatch]);

  useEffect(() => {
    let active = true;
    setNoTicketReturns({ items: [], loading: true, error: false });
    if (!shift?.id) return () => { active = false; };
    customerReturnService.list({ shiftId: shift.id }).then(({ data }) => {
      if (active) setNoTicketReturns({
        items: (data.body || []).filter(item => !item.numBL?.trim() && ['VALIDE', 'COMPTABILISE'].includes(item.statut)),
        loading: false, error: false,
      });
    }).catch(() => { if (active) setNoTicketReturns({ items: [], loading: false, error: true }); });
    return () => { active = false; };
  }, [shift?.id, returnsRefresh]);

  useEffect(() => {
    const search = salesSearch.trim();
    setSalesFilters((filters) => ({
      ...filters,
      page: 0,
      query: search && !isNumericSearch(search) ? search : undefined,
      amount: isNumericSearch(search) ? search.replace(",", ".") : undefined,
      paymentFilter: salesPaymentFilter !== "all" ? salesPaymentFilter : undefined,
      sortField: salesSort.field || undefined,
      sortDirection: salesSort.field ? salesSort.direction : undefined,
    }));
  }, [salesSearch, salesPaymentFilter, salesSort]);

  useEffect(() => {
    setSalesFilters((filters) => filters.page === 0 ? filters : { ...filters, page: 0 });
  }, [shift?.id]);

  useEffect(() => {
    if (!shift?.id) return;
    dispatch(deliveryActions.list({ shiftId: shift.id, ...salesFilters }));
  }, [shift?.id, salesFilters, dispatch]);

  useEffect(() => {
    let active = true;
    setLinkedReturns({ data: {}, loading: true, error: false });
    if (deliveryLoading) return () => { active = false; };
    const numbers = JSON.parse(references);
    if (!numbers.length) {
      setLinkedReturns({ data: {}, loading: false, error: false });
      return () => { active = false; };
    }
    customerReturnService.linked(numbers).then(({ data }) => {
      if (active) setLinkedReturns({ data: data || {}, loading: false, error: false });
    }).catch(() => {
      if (active) setLinkedReturns({ data: {}, loading: false, error: true });
    });
    return () => { active = false; };
  }, [references, deliveryLoading, shift?.id]);

  useEffect(() => {
    if (!summary) return;
    setClosingCash(summary.closingAmountCash || 0);
    setClosingCard(summary.closingAmountCard || 0);
  }, [summary]);

  const cashDifference = useMemo(
    () => Number(closingCash || 0) - Number(summary?.expectedCashAmount || 0),
    [closingCash, summary]
  );
  const cardDifference = useMemo(
    () => Number(closingCard || 0) - Number(summary?.expectedCardAmount || 0),
    [closingCard, summary]
  );

  const financial = shiftFinancialTotals(summary || {});
  const expectedTotal = Number(summary?.expectedCashAmount || 0) + Number(summary?.expectedCardAmount || 0);
  const closingTotal = Number(closingCash || 0) + Number(closingCard || 0);

  const salesRows = list;

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
  };

  const generateThermalReceipt = async (ticketData) => {
    try {
      const measureDoc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, 350],
      });
      const estimatedAddressHeight = address
        ? measureDoc.splitTextToSize(address, 72).slice(0, 3).length * 3.3
        : 0;
      const estimatedItemsHeight = (ticketData?.lignes || []).reduce((height, item) => {
        const nameLines = measureDoc.splitTextToSize(item.articleNom || "-", 38);
        return height + 3.8 + Math.max(nameLines.length - 1, 0) * 3.4;
      }, 0);
      const payment = getReceiptPaymentSummary(ticketData);
      const receiptHeight = Math.ceil(
        265 +
        estimatedAddressHeight +
        (telephone ? 3.5 : 0) +
        estimatedItemsHeight +
        (payment.stampDuty > 0 ? 4.2 : 0) +
        (payment.change > 0 ? 4.2 : 0)
      );
      const thermalPageHeight = Math.min(Math.max(receiptHeight, 230), 280);
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [80, thermalPageHeight],
      });

      const pageWidth = 80;
      const margin = 4;
      const contentWidth = pageWidth - margin * 2;
      const right = pageWidth - margin;
      let y = 7;
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 8;

      const ensureSpace = (height = 0) => {
        if (y + height <= pageHeight - bottomMargin) return;
        doc.addPage([pageWidth, pageHeight], "portrait");
        y = 7;
      };

      const drawRule = (bold = false) => {
        ensureSpace(bold ? 4 : 3.5);
        doc.setDrawColor(0);
        doc.setLineWidth(bold ? 0.35 : 0.12);
        doc.line(margin, y, right, y);
        y += bold ? 3.6 : 3;
      };

      const drawRow = (label, value, { bold = false, size = 9.5 } = {}) => {
        ensureSpace(size >= 11 ? 5.5 : 4.7);
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.text(label, margin, y);
        doc.text(formatPrice(value, false), right, y, { align: "right" });
        y += size >= 11 ? 5 : 4.2;
      };

      const drawInfo = (label, value) => {
        ensureSpace(4.2);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.2);
        doc.text(`${label}:`, margin, y);
        doc.text(String(value || "-"), margin + 15, y, { maxWidth: contentWidth - 15 });
        y += 3.8;
      };

      const totalQuantity = (ticketData?.lignes || []).reduce(
        (sum, item) => sum + Number(item.quantiteLivree || 0),
        0
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text(businessName || "MATJARPOS", pageWidth / 2, y, { align: "center" });
      y += 5.5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      if (address) {
        doc.splitTextToSize(address, contentWidth).slice(0, 3).forEach((line) => {
          doc.text(line, pageWidth / 2, y, { align: "center" });
          y += 3.3;
        });
      }
      if (telephone) {
        doc.text(`Tel: ${telephone}`, pageWidth / 2, y, { align: "center" });
        y += 3.5;
      }

      drawRule(true);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("TICKET DE CAISSE", pageWidth / 2, y, { align: "center" });
      y += 5;

      drawInfo("Ticket", ticketData?.numero);
      drawInfo("Date", ticketData?.createdAt || ticketData?.dateOperation);
      drawInfo("Client", ticketData?.clientNom || "Passager");
      drawRule();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.text("DESIGNATION", margin, y);
      doc.text("QTE", 47, y, { align: "right" });
      doc.text("P.U.", 59, y, { align: "right" });
      doc.text("TOTAL", right, y, { align: "right" });
      y += 3.5;
      drawRule();

      (ticketData?.lignes || []).forEach((item) => {
        const qte = Number(item.quantiteLivree || 0);
        const pu = Number(item.prixUnitaireTTC || 0);
        const total = Number(item.montantTTC || qte * pu);
        const nameLines = doc.splitTextToSize(item.articleNom || "-", 38);
        ensureSpace(3.8 + Math.max(nameLines.length - 1, 0) * 3.4);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.text(nameLines[0], margin, y);
        doc.text(String(qte), 47, y, { align: "right" });
        doc.text(formatPrice(pu, false), 59, y, { align: "right" });
        doc.text(formatPrice(total, false), right, y, { align: "right" });
        y += 3.8;

        if (nameLines.length > 1) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          nameLines.slice(1).forEach((line) => {
            doc.text(line, margin + 2, y);
            y += 3.4;
          });
        }
      });

      ensureSpace(90);
      drawRule();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(`${ticketData?.lignes?.length || 0} article(s)`, margin, y);
      doc.text(`${totalQuantity} piece(s)`, right, y, { align: "right" });
      y += 4.2;
      drawRule(true);

      drawRow("TOTAL TTC", ticketData?.montantTotalTTC || 0, { bold: true, size: 11 });
      if (payment.stampDuty > 0) {
        drawRow("Droit de timbre 0,25%", payment.stampDuty);
      }

      ensureSpace(10);
      doc.setFillColor(0, 0, 0);
      doc.rect(margin, y - 0.5, contentWidth, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("NET A PAYER", margin + 2, y + 4.2);
      doc.text(formatPrice(payment.totalDue, false), right - 2, y + 4.2, { align: "right" });
      doc.setTextColor(0, 0, 0);
      y += 9.5;

      ensureSpace(34);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("PAIEMENT", margin, y);
      y += 4.2;
      drawInfo("Mode", getModeReglementLabel(ticketData?.modeReglement));
      drawRow("Especes", payment.cash);
      drawRow("Carte", payment.card);
      drawRow("Montant paye", payment.paid, { bold: true });
      if (payment.remaining > 0) drawRow("Reste a payer", payment.remaining, { bold: true });
      if (payment.change > 0) {
        drawRow("Monnaie", payment.change, { bold: true });
      }

      ensureSpace(18);
      drawRule();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text("Merci de votre confiance", pageWidth / 2, y, { align: "center" });
      y += 4;
      doc.text("A bientot", pageWidth / 2, y, { align: "center" });
     

	      printPdfInHiddenIframe(doc.output("bloburl"));
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("sale.error"),
        text: "Impossible d'imprimer le ticket.",
      });
    }
  };

  const statusBadge = () => {
    if (isValidated) return <Badge color="success" pill>Vérifiée</Badge>;
    if (isClosed) return <Badge color="secondary" pill>À vérifier</Badge>;
    return <Badge color="warning" pill>En cours</Badge>;
  };

  const diffColor = (value) => {
    if (Number(value) === 0) return "success";
    return Number(value) > 0 ? "warning" : "danger";
  };

  const handleValidate = () => {
    if (![closingCash, closingCard].every((value) => value !== "" && Number.isFinite(Number(value)) && Number(value) >= 0)) {
      Swal.fire({ icon: "error", title: "Vérifiez les montants", text: "Saisissez un montant égal ou supérieur à zéro pour les espèces et la carte." });
      return;
    }
    Swal.fire({
      title: "Confirmer les montants de la caisse ?",
      text: `Espèces : ${formatPrice(closingCash)}. Carte : ${formatPrice(closingCard)}. Ces montants ne pourront plus être modifiés.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Oui, confirmer",
      cancelButtonText: "Annuler",
      reverseButtons: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    }).then((result) => {
      if (!result.isConfirmed) return;
      dispatch(
        shiftActions.validateShift({
          id: summary.shiftId,
          data: {
            isValidated: true,
            closingAmountCash: Number(closingCash),
            closingAmountCard: Number(closingCard),
          },
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.updated"),
              text: t("shifts.updatedSuccess"),
            });
            navigate(-1);
          },
        })
      );
    });
  };

  const MetricCard = ({ icon, label, value, detail, tone = "" }) => (
    <div className="shift-kpi">
      <span className={tone}>{icon}</span>
      <span>{label}</span>
      <strong>{value == null ? "—" : formatPrice(value)}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );

  if (!shift?.id) {
    return <Container fluid className="shift-page"><div className="shift-empty">Choisissez une session depuis la liste des caisses.<Button color="primary" onClick={() => navigate(-1)}><FaArrowLeft /> Retour</Button></div></Container>;
  }

  if (!summary || summary.shiftId !== shift.id || loading) {
    return (
      <Container fluid className="shift-page">
        <div className="shift-empty">
          <Spinner color="primary" />
          <div>{t("common.loading")}</div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="shift-page">
      <div className="shift-header">
        <div>
          <div className="shift-title">
            <FaUserClock />
            Session de caisse {shift?.shiftCode || summary.shiftCode}
          </div>
          <div className="shift-subtitle">
            {summary.commercialName} · Début : {formatDateTime(summary.openingTime)}
            {summary.closingTime && ` · Fin : ${formatDateTime(summary.closingTime)}`}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {statusBadge()}
          <Button color="light" className="shift-action-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Retour
          </Button>
        </div>
      </div>

      <Row className="g-2 mb-2">
        <Col sm={6} xl={3}>
          <MetricCard icon={<FaCashRegister />} label="Ventes après retours" value={financial.sales} tone="text-primary" />
        </Col>
        <Col sm={6} xl={3}>
          <MetricCard icon={<FaUndoAlt />} label="Articles retournés — tous les retours" value={financial.returns}
            detail={`Sans ticket : ${formatPrice(financial.noTicketReturns)}`} tone="text-warning" />
        </Col>
        <Col sm={6} xl={3}>
          <MetricCard icon={<FaMoneyBillWave />} label="Argent reçu après remboursements" value={financial.payments} tone="text-success" />
        </Col>
        <Col sm={6} xl={3}>
          <MetricCard icon={<FaUserClock />} label="Crédit client — reste impayé" value={financial.clientOutstanding} tone="text-warning" />
        </Col>
      </Row>
      <p className="shift-help">Reste actuel sur les ventes livrées de cette session. Les avoirs non affectés ne sont pas déduits.</p>

      <Row className="g-2 mb-2">
        <Col md={6}>
          <Card className="shift-list-card h-100">
            <CardBody>
              <div className="shift-detail-section-title">
                <FaMoneyBillWave />
                Espèces
              </div>
              <div className="shift-payment-grid">
                <div>
                  <span>Argent reçu</span>
                  <strong>{formatPrice(summary.totalCashPayments)}</strong>
                </div>
                <div>
                  <span>Remboursements</span>
                  <strong>{formatPrice(financial.cashRefunds)}</strong>
                </div>
                <div>
                  <span>À trouver dans la caisse</span>
                  <strong>{formatPrice(summary.expectedCashAmount)}</strong>
                </div>
              </div>
              {(isClosed || isValidated) && <div className="shift-close-edit">
                <div>
                <Label for="shift-closing-cash">Espèces comptées (DH)</Label>
                <Input
                  id="shift-closing-cash"
                  type="number"
                  min="0" step="0.01" inputMode="decimal"
                  disabled={!editMode || !isClosed || isValidated}
                  value={closingCash}
                  onChange={(e) => setClosingCash(e.target.value)}
                />
                </div>
                <Badge color={diffColor(cashDifference)}>{differenceLabel(cashDifference)}</Badge>
              </div>}
            </CardBody>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shift-list-card h-100">
            <CardBody>
              <div className="shift-detail-section-title">
                <FaCreditCard />
                Carte bancaire
              </div>
              <div className="shift-payment-grid">
                <div>
                  <span>Argent reçu</span>
                  <strong>{formatPrice(summary.totalCardPayments)}</strong>
                </div>
                <div>
                  <span>Remboursements</span>
                  <strong>{formatPrice(financial.cardRefunds)}</strong>
                </div>
                <div>
                  <span>À trouver sur le terminal</span>
                  <strong>{formatPrice(summary.expectedCardAmount)}</strong>
                </div>
              </div>
              {(isClosed || isValidated) && <div className="shift-close-edit">
                <div>
                <Label for="shift-closing-card">Total du terminal carte (DH)</Label>
                <Input
                  id="shift-closing-card"
                  type="number"
                  min="0" step="0.01" inputMode="decimal"
                  disabled={!editMode || !isClosed || isValidated}
                  value={closingCard}
                  onChange={(e) => setClosingCard(e.target.value)}
                />
                </div>
                <Badge color={diffColor(cardDifference)}>{differenceLabel(cardDifference)}</Badge>
              </div>}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {(isClosed || isValidated) ? <Card className="shift-list-card mb-2">
        <CardBody>
          <div className="shift-verification-header">
            <div className="shift-detail-section-title mb-0">
              <FaCheckCircle /> Vérification de la caisse
            </div>
            {editMode && isClosed && !isValidated && (
              <Button color="success" onClick={handleValidate} disabled={loading} className="fw-bold">
                {loading && <Spinner size="sm" className="me-2" />}
                <FaCheckCircle className="me-2" /> Confirmer les montants
              </Button>
            )}
          </div>
          <p className="shift-help">Comparez les espèces comptées et le total du terminal aux montants à trouver.</p>
          <Row className="g-2">
            <Col md={4}>
              <div className="shift-total-box">
                <span>Total à trouver</span>
                <strong>{formatPrice(expectedTotal)}</strong>
              </div>
            </Col>
            <Col md={4}>
              <div className="shift-total-box">
                <span>Total compté</span>
                <strong>{formatPrice(closingTotal)}</strong>
              </div>
            </Col>
            <Col md={4}>
              <div className="shift-total-box">
                <span>Résultat de la vérification</span>
                <Badge color={diffColor(closingTotal - expectedTotal)}>{differenceLabel(closingTotal - expectedTotal)}</Badge>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card> : <p className="shift-help">Cette caisse est en cours. Les montants pourront être vérifiés après sa fermeture.</p>}

      <Card className="shift-list-card">
        <CardBody className="p-0">
          <div className="shift-sales-header">
            <div>
              <div className="shift-detail-section-title mb-1"><FaReceipt /> Ventes et retours</div>
              <p className="shift-help mb-0">Les retours confirmés suivent leur BL, même s'ils ont été faits dans une autre session.</p>
            </div>
            <DocumentPagination
              pageNumber={pageNumber ?? salesFilters.page}
              totalPages={totalPages}
              totalElements={totalElements}
              pageSize={pageSize ?? salesFilters.size}
              onPageChange={(page) => setSalesFilters((filters) => ({ ...filters, page }))}
            />
          </div>
          <div className="shift-document-toolbar">
            <div>
              <Label for="shift-sales-search">Chercher une vente</Label>
              <Input id="shift-sales-search" type="search" value={salesSearch}
                onChange={(event) => setSalesSearch(event.target.value)} placeholder="Numéro du BL, client ou montant" />
            </div>
            <div>
              <Label for="shift-payment-filter">Paiement</Label>
              <Input id="shift-payment-filter" type="select" value={salesPaymentFilter}
                onChange={(event) => setSalesPaymentFilter(event.target.value)}>
                <option value="all">Tous les paiements</option>
                <option value="cash">Espèces</option>
                <option value="card">Carte bancaire</option>
                <option value="credit">Crédit client — reste impayé</option>
              </Input>
            </div>
            <div>
              <Label for="shift-sales-sort">Ordre des ventes</Label>
              <Input id="shift-sales-sort" type="select" value={salesSort.field ? `${salesSort.field}:${salesSort.direction}` : ""}
                onChange={(event) => {
                  const [field, direction] = event.target.value.split(":");
                  setSalesSort({ field: field || null, direction: direction || "desc" });
                }}>
                <option value="">Ordre habituel</option>
                {Object.entries(amountSortLabels).map(([field, label]) => (
                  <Fragment key={field}>
                    <option value={`${field}:desc`}>{label} : du plus grand au plus petit</option>
                    <option value={`${field}:asc`}>{label} : du plus petit au plus grand</option>
                  </Fragment>
                ))}
              </Input>
            </div>
          </div>
          {deliveryLoading ? (
            <div className="shift-empty"><Spinner color="primary" /><div>Chargement des ventes…</div></div>
          ) : salesRows.length ? (
            <div className="shift-table-wrap" role="region" aria-label="Ventes et retours des bons de livraison" tabIndex={0}>
              <table className="shift-documents-table">
                <caption className="visually-hidden">Bons de livraison et retours associés. Tous les montants sont en DH.</caption>
                <thead>
                  <tr>
                    <th scope="col">Vente / retour</th>
                    <th scope="col">Client et date</th>
                    <th scope="col">Montant des articles</th>
                    <th scope="col">Payé / remboursé</th>
                    <th scope="col">Espèces</th>
                    <th scope="col">Carte</th>
                    <th scope="col">Reste impayé</th>
                    <th scope="col">État / action</th>
                  </tr>
                </thead>
                <tbody>
                  {salesRows.map((bl) => {
                    const returns = linkedReturns.data[bl.numero] || [];
                    return (
                      <Fragment key={bl.id}>
                        <tr>
                          <th scope="row"><span className="shift-document-kind"><FaReceipt /> Vente</span><strong>{bl.numero}</strong>
                            <small>{linkedReturns.loading ? "Chargement des retours…" : linkedReturns.error ? "Retours indisponibles" : returns.length ? `${returns.length} retour(s)` : "Aucun retour"}</small>
                          </th>
                          <td><strong>{bl.clientNom || "Client de passage"}</strong><small>{formatDateTime(bl.createdAt || bl.dateOperation)}</small></td>
                          <td className="shift-money">{formatPrice(bl.montantTotalTTC, false)} DH</td>
                          <td className="shift-money">{formatPrice(bl.montantPaye ?? getCashAmount(bl) + getCardAmount(bl), false)} DH</td>
                          <td className="shift-money">{formatPrice(getCashAmount(bl), false)} DH</td>
                          <td className="shift-money">{formatPrice(getCardAmount(bl), false)} DH</td>
                          <td className="shift-money text-warning">{deliveryOutstanding(bl) == null ? "—" : formatPrice(deliveryOutstanding(bl))}</td>
                          <td>
                            <Badge color={bl.status === "LIVRE" ? "success" : "secondary"}>{documentStatus(bl.status)}</Badge>
                            <Button color="light" className="shift-ticket-button" onClick={() => generateThermalReceipt(bl)} aria-label={`Imprimer le ticket ${bl.numero}`}><FaPrint /> Imprimer</Button>
                          </td>
                        </tr>
                        {returns.map((item) => (
                          <tr className="shift-return-table-row" key={`${bl.id}-${item.id}`}>
                            <th scope="row"><span className="shift-document-kind"><FaUndoAlt /> Retour</span><strong>{item.numeroRetour}</strong><small>Pour le BL {bl.numero}</small></th>
                            <td><strong>{item.clientNom || bl.clientNom || "Client de passage"}</strong><small>{formatDateTime(item.dateOperation)}</small></td>
                            <td className="shift-money">{formatPrice(item.montantTTC, false)} DH</td>
                            <td className="shift-money">{formatPrice(getReturnCashAmount(item) + getReturnCardAmount(item), false)} DH
                              {Number(item.montantTTC || 0) > getReturnCashAmount(item) + getReturnCardAmount(item) && <small>Avoir : {formatPrice(Number(item.montantTTC || 0) - getReturnCashAmount(item) - getReturnCardAmount(item), false)} DH</small>}
                            </td>
                            <td className="shift-money">{formatPrice(getReturnCashAmount(item), false)} DH</td>
                            <td className="shift-money">{formatPrice(getReturnCardAmount(item), false)} DH</td>
                            <td>—</td>
                            <td><Badge color="success">{documentStatus(item.statut)}</Badge></td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <div className="shift-empty">Aucune vente trouvée. Essayez une autre recherche ou un autre paiement.</div>}
          {linkedReturns.error && <p role="alert" className="text-danger p-3 mb-0">Impossible de charger les retours. Rechargez la page pour réessayer.</p>}
        </CardBody>
      </Card>
      <Card className="shift-list-card mt-3">
        <CardBody>
          <div className="shift-detail-section-title"><FaUndoAlt /> Retours sans ticket de cette session</div>
          <p className="shift-help">Tous les retours confirmés sans ticket, y compris ceux qui réduisent le crédit client sans remboursement.</p>
          {noTicketReturns.loading ? <div className="shift-empty" role="status"><Spinner color="primary" />Chargement des retours sans ticket…</div>
            : noTicketReturns.error ? <div role="alert" className="text-danger">Impossible de charger les retours sans ticket. <Button color="light" onClick={() => setReturnsRefresh(value => value + 1)}>Réessayer</Button></div>
              : !noTicketReturns.items.length ? <div className="shift-empty">Aucun retour sans ticket confirmé dans cette session.</div>
                : <div className="shift-table-wrap" role="region" aria-label="Retours sans ticket" tabIndex={0}>
                  <table className="shift-documents-table">
                    <caption className="visually-hidden">Retours sans ticket de cette session. Tous les montants sont en DH.</caption>
                    <thead><tr><th scope="col">Retour</th><th scope="col">Client et date</th><th scope="col">Montant des articles</th><th scope="col">Remboursé</th><th scope="col">Espèces</th><th scope="col">Carte</th><th scope="col">Crédit / avoir</th><th scope="col">État</th></tr></thead>
                    <tbody>{noTicketReturns.items.map(item => <tr key={item.id} className="shift-return-table-row">
                      <th scope="row"><span className="shift-document-kind"><FaUndoAlt /> Sans ticket</span><strong>{item.numeroRetour}</strong></th>
                      <td><strong>{item.clientNom || "Client de passage"}</strong><small>{formatDateTime(item.dateOperation)}</small></td>
                      <td className="shift-money">{formatPrice(item.montantTTC)}</td>
                      <td className="shift-money">{formatPrice(getReturnCashAmount(item) + getReturnCardAmount(item))}</td>
                      <td className="shift-money">{formatPrice(getReturnCashAmount(item))}</td>
                      <td className="shift-money">{formatPrice(getReturnCardAmount(item))}</td>
                      <td className="shift-money">{formatPrice(Math.max(0, Number(item.montantTTC || 0) - getReturnCashAmount(item) - getReturnCardAmount(item)))}</td>
                      <td><Badge color="success">{documentStatus(item.statut)}</Badge></td>
                    </tr>)}</tbody>
                  </table>
                </div>}
        </CardBody>
      </Card>
    </Container>
  );
};

export default ShiftDetails;
